import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, FileText, Calendar, Filter, Search, AlertTriangle, ChevronLeft, ChevronRight, Users, ChevronDown, ChevronUp, KeyRound, Mail, Scale, UserPlus, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';
import AdminDossierDetail from '@/components/AdminDossierDetail';
import AdminPartialSubmissions from '@/components/AdminPartialSubmissions';
import AdminLoginAttempts from '@/components/AdminLoginAttempts';
import AdminEmails from '@/components/AdminEmails';
import AdminPromoCodes from '@/components/AdminPromoCodes';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showPartialSubmissions, setShowPartialSubmissions] = useState(false);
  const [showLoginAttempts, setShowLoginAttempts] = useState(false);
  const [showEmails, setShowEmails] = useState(false);
  const [showPromoCodes, setShowPromoCodes] = useState(false);
  const [showLawyers, setShowLawyers] = useState(false);
  const [lawyerForm, setLawyerForm] = useState({ nom: '', email: '', password: '', telephone: '' });
  const [creatingLawyer, setCreatingLawyer] = useState(false);
  const [lawyers, setLawyers] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    minScore: '',
    maxScore: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.is_admin) {
      toast.error('Acces non autorise');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [filters, pagination]);

  const fetchData = async () => {
    try {
      const params = {
        limit: pagination.limit,
        offset: pagination.offset
      };
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      // L'admin tape sur /100, on convertit en /9.5 pour le backend (qui compare au score brut)
      if (filters.minScore) params.min_score = parseFloat(filters.minScore) / 10;
      if (filters.maxScore) params.max_score = parseFloat(filters.maxScore) / 10;
      if (filters.search) params.search = filters.search;

      const [dossiersResponse, statsResponse] = await Promise.all([
        adminAPI.getAllDossiers(params),
        adminAPI.getStats()
      ]);
      setDossiers(dossiersResponse.data.dossiers || dossiersResponse.data);
      setTotal(dossiersResponse.data.total || dossiersResponse.data.length);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const handleDossierClick = (dossier) => {
    setSelectedDossier(dossier);
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedDossier(null);
  };

  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < total) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const fetchLawyers = async () => {
    try {
      const res = await adminAPI.getLawyers();
      setLawyers(res.data.lawyers || []);
    } catch (error) {
      console.error('Error fetching lawyers:', error);
    }
  };

  const handleCreateLawyer = async (e) => {
    e.preventDefault();
    if (!lawyerForm.nom || !lawyerForm.email || !lawyerForm.password) {
      toast.error('Nom, email et mot de passe requis');
      return;
    }
    setCreatingLawyer(true);
    try {
      await adminAPI.createLawyer(lawyerForm);
      toast.success('Compte avocat créé');
      setLawyerForm({ nom: '', email: '', password: '', telephone: '' });
      fetchLawyers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingLawyer(false);
    }
  };

  const getScoreColor = (score) => {
    // score brut IA : 0-9.5 (NOT 0-100). On compare sur l'échelle native.
    if (score <= 3) return 'text-red-600';
    if (score <= 7) return 'text-orange-600';
    return 'text-green-600';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield size={32} color="#1e3a8a" strokeWidth={2.5} />
              <h1 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                Admin - Recouvrement Protect
              </h1>
            </div>
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={handleLogout}
            >
              <LogOut className="mr-2" size={20} />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        {stats && (
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="card p-6">
              <p className="text-sm text-gray-500 mb-1">Total Dossiers</p>
              <p className="text-3xl font-bold" style={{ color: '#1e3a8a' }}>
                {stats.total_dossiers}
              </p>
            </div>
            <div className="card p-6">
              <p className="text-sm text-gray-500 mb-1">Dossiers Payés</p>
              <p className="text-3xl font-bold text-green-600">
                {stats.paid_dossiers}
              </p>
            </div>
            <div className="card p-6">
              <p className="text-sm text-gray-500 mb-1">En Attente</p>
              <p className="text-3xl font-bold text-yellow-600">
                {stats.pending_dossiers}
              </p>
            </div>
          </div>
        )}

        {/* Partial Submissions - Contacts for qualitative interviews */}
        <div className="card p-6 mb-6">
          <button
            onClick={() => setShowPartialSubmissions(!showPartialSubmissions)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Users size={20} color="#1e3a8a" />
              <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
                Interviews Qualitatives
              </h3>
              <span className="text-sm text-gray-500">(formulaires abandonnes)</span>
            </div>
            {showPartialSubmissions ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </button>
          {showPartialSubmissions && (
            <div className="mt-4 pt-4 border-t">
              <AdminPartialSubmissions />
            </div>
          )}
        </div>

        {/* Login Attempts - Detect login issues */}
        <div className="card p-6 mb-6">
          <button
            onClick={() => setShowLoginAttempts(!showLoginAttempts)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <KeyRound size={20} color="#1e3a8a" />
              <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
                Problemes de Connexion
              </h3>
              <span className="text-sm text-gray-500">(tentatives echouees)</span>
            </div>
            {showLoginAttempts ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </button>
          {showLoginAttempts && (
            <div className="mt-4 pt-4 border-t">
              <AdminLoginAttempts />
            </div>
          )}
        </div>

        {/* Email Automation */}
        <div className="card p-6 mb-6">
          <button
            onClick={() => setShowEmails(!showEmails)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Mail size={20} color="#1e3a8a" />
              <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
                Automatisation Emails
              </h3>
              <span className="text-sm text-gray-500">(templates, campagnes, logs)</span>
            </div>
            {showEmails ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </button>
          {showEmails && (
            <div className="mt-4 pt-4 border-t">
              <AdminEmails />
            </div>
          )}
        </div>

        {/* Lawyer Management */}
        <div className="card p-6 mb-6">
          <button
            onClick={() => { setShowLawyers(!showLawyers); if (!showLawyers) fetchLawyers(); }}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Scale size={20} color="#1e3a8a" />
              <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
                Gestion Avocats
              </h3>
              <span className="text-sm text-gray-500">(marketplace leads)</span>
            </div>
            {showLawyers ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </button>
          {showLawyers && (
            <div className="mt-4 pt-4 border-t space-y-6">
              {/* Create lawyer form */}
              <form onSubmit={handleCreateLawyer} className="p-4 bg-blue-50 rounded-lg space-y-3">
                <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                  <UserPlus size={18} />
                  Créer un compte avocat
                </h4>
                <div className="grid md:grid-cols-2 gap-3">
                  <Input
                    placeholder="Nom complet"
                    value={lawyerForm.nom}
                    onChange={(e) => setLawyerForm({ ...lawyerForm, nom: e.target.value })}
                    required
                  />
                  <Input
                    type="email"
                    placeholder="Email"
                    value={lawyerForm.email}
                    onChange={(e) => setLawyerForm({ ...lawyerForm, email: e.target.value })}
                    required
                  />
                  <Input
                    type="password"
                    placeholder="Mot de passe"
                    value={lawyerForm.password}
                    onChange={(e) => setLawyerForm({ ...lawyerForm, password: e.target.value })}
                    required
                  />
                  <Input
                    placeholder="Téléphone (optionnel)"
                    value={lawyerForm.telephone}
                    onChange={(e) => setLawyerForm({ ...lawyerForm, telephone: e.target.value })}
                  />
                </div>
                <Button type="submit" disabled={creatingLawyer}>
                  {creatingLawyer ? 'Création...' : 'Créer le compte'}
                </Button>
              </form>

              {/* Lawyers list */}
              {lawyers.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3">Comptes avocats ({lawyers.length})</h4>
                  <div className="space-y-2">
                    {lawyers.map((lawyer) => (
                      <div key={lawyer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium">{lawyer.nom}</span>
                          <span className="text-sm text-gray-500 ml-3">{lawyer.email}</span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(lawyer.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Promo Codes */}
        <div className="card p-6 mb-6">
          <button
            onClick={() => setShowPromoCodes(!showPromoCodes)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <Tag size={20} color="#1e3a8a" />
              <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
                Codes Promo
              </h3>
              <span className="text-sm text-gray-500">(réductions marketplace avocats)</span>
            </div>
            {showPromoCodes ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </button>
          {showPromoCodes && (
            <div className="mt-4 pt-4 border-t">
              <AdminPromoCodes />
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="card p-6 mb-6">
          <div className="flex items-center mb-4">
            <Filter className="mr-2" size={20} color="#1e3a8a" />
            <h3 className="text-lg font-semibold" style={{ color: '#1e3a8a' }}>
              Filtres
            </h3>
          </div>
          {/* Search */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recherche (nom, email, telephone)
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                data-testid="filter-search"
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                placeholder="Rechercher un dossier..."
                className="pl-10"
              />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut de paiement
              </label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="paid">Payé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Score minimum
              </label>
              <Input
                data-testid="filter-min-score"
                type="number"
                min="0"
                max="100"
                value={filters.minScore}
                onChange={(e) => setFilters({ ...filters, minScore: e.target.value })}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Score maximum
              </label>
              <Input
                data-testid="filter-max-score"
                type="number"
                min="0"
                max="100"
                value={filters.maxScore}
                onChange={(e) => setFilters({ ...filters, maxScore: e.target.value })}
                placeholder="100"
              />
            </div>
          </div>
          <Button
            data-testid="clear-filters-button"
            variant="outline"
            onClick={() => {
              setFilters({ status: 'all', minScore: '', maxScore: '', search: '' });
              setPagination({ offset: 0, limit: 20 });
            }}
            className="mt-4"
          >
            Reinitialiser les filtres
          </Button>
        </div>

        {/* Dossiers List */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
            Tous les dossiers ({total})
          </h3>
          <span className="text-sm text-gray-500">
            Affichage {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, total)} sur {total}
          </span>
        </div>

        {dossiers.length === 0 ? (
          <div className="card p-12 text-center">
            <FileText size={64} color="#9ca3af" className="mx-auto mb-4" />
            <p className="text-gray-600">Aucun dossier trouvé</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {dossiers.map((dossier) => {
              const hasInvalidDocs = dossier.documents_validity && !dossier.documents_validity.is_valid;
              const hasComment = !!(dossier.admin_comment && dossier.admin_comment.trim());
              return (
              <div
                key={dossier.id}
                data-testid={`admin-dossier-card-${dossier.id}`}
                className="card p-6 cursor-pointer hover:shadow-lg transition-shadow"
                style={hasComment ? { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' } : undefined}
                onClick={() => handleDossierClick(dossier)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <FileText size={24} color="#1e3a8a" />
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-800">
                            {dossier.nom} - #{dossier.id.slice(0, 8)}
                          </h4>
                          {hasInvalidDocs && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                              <AlertTriangle size={12} />
                              Docs invalides
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{dossier.email}</p>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <Calendar size={14} className="mr-1" />
                          {formatDate(dossier.created_at)}
                        </div>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-sm text-gray-500">Score</p>
                        {dossier.score !== null ? (
                          <p className={`text-2xl font-bold ${getScoreColor(dossier.score)}`}>
                            {Math.round((dossier.score || 0) * 10)}/100
                          </p>
                        ) : (
                          <p className="text-sm text-gray-400">En cours...</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Fichiers</p>
                        <p className="font-medium text-gray-800">
                          {dossier.files?.length || 0} document(s)
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Téléphone</p>
                        <p className="font-medium text-gray-800">{dossier.telephone}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Statut</p>
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            dossier.payment_status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {dossier.payment_status === 'paid' ? 'Payé' : 'En attente'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > pagination.limit && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              onClick={handlePrevPage}
              disabled={pagination.offset === 0}
            >
              <ChevronLeft size={18} className="mr-1" />
              Precedent
            </Button>
            <span className="text-sm text-gray-600">
              Page {Math.floor(pagination.offset / pagination.limit) + 1} sur {Math.ceil(total / pagination.limit)}
            </span>
            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={pagination.offset + pagination.limit >= total}
            >
              Suivant
              <ChevronRight size={18} className="ml-1" />
            </Button>
          </div>
        )}

        {/* Detail Modal */}
        <AdminDossierDetail
          dossier={selectedDossier}
          open={showDetail}
          onClose={handleCloseDetail}
          onDossierUpdated={fetchData}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
